import React, { createContext, useContext, useState, useCallback } from 'react';
import type { KeywordSuggestion } from '@/hooks/useKeywordAssistant';

export interface KeywordAssistantEditorFormState {
  topic: string;
  selectedKeywords: string[];
  selectedCountries: string[];
  researchProgress: { current: number; total: number } | null;
  openAIKeywords: string[];
  suggestions: KeywordSuggestion[];
}

const initialFormState: KeywordAssistantEditorFormState = {
  topic: '',
  selectedKeywords: [],
  selectedCountries: [],
  researchProgress: null,
  openAIKeywords: [],
  suggestions: [],
};

interface KeywordAssistantEditorContextType {
  isOpen: boolean;
  formState: KeywordAssistantEditorFormState;
  setFormState: React.Dispatch<React.SetStateAction<KeywordAssistantEditorFormState>>;
  openEditor: () => void;
  closeEditor: () => void;
  resetEditor: () => void;
  updateFormField: <K extends keyof KeywordAssistantEditorFormState>(
    field: K,
    value: KeywordAssistantEditorFormState[K]
  ) => void;
}

const KeywordAssistantEditorContext = createContext<KeywordAssistantEditorContextType | undefined>(undefined);

export function KeywordAssistantEditorProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState<KeywordAssistantEditorFormState>(initialFormState);

  const openEditor = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetEditor = useCallback(() => {
    setFormState(initialFormState);
  }, []);

  const updateFormField = useCallback(<K extends keyof KeywordAssistantEditorFormState>(
    field: K,
    value: KeywordAssistantEditorFormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <KeywordAssistantEditorContext.Provider
      value={{
        isOpen,
        formState,
        setFormState,
        openEditor,
        closeEditor,
        resetEditor,
        updateFormField,
      }}
    >
      {children}
    </KeywordAssistantEditorContext.Provider>
  );
}

export function useKeywordAssistantEditor() {
  const context = useContext(KeywordAssistantEditorContext);
  if (!context) {
    throw new Error('useKeywordAssistantEditor must be used within KeywordAssistantEditorProvider');
  }
  return context;
}
